import type * as THREE from 'three';
import type { FixtureResult } from './physics.ts';

interface MemoryInfo { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number }

export interface LabMetrics {
  generatedAt: string;
  route: string;
  environment: Record<string, string | number | boolean | null>;
  render: Record<string, string | number | null>;
  theme: Record<string, string | number | boolean>;
  media: Record<string, string | number | boolean | null>;
  fixtures: FixtureResult[];
  notes: string[];
}

export class MetricsCollector {
  private frames: number[] = [];
  private lastFrame = performance.now();
  media: LabMetrics['media'] = {};
  notes: string[] = [];

  frame(now: number): void {
    this.frames.push(now - this.lastFrame);
    this.lastFrame = now;
    if (this.frames.length > 600) this.frames.shift();
  }

  snapshot(
    route: string,
    renderer: THREE.WebGLRenderer,
    fixtures: FixtureResult[],
    theme: Record<string, string | number | boolean>,
  ): LabMetrics {
    const sorted = [...this.frames].sort((a, b) => a - b);
    const percentile = (p: number) => sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))] : 0;
    const memory = (performance as Performance & { memory?: MemoryInfo }).memory;
    const gl = renderer.getContext();
    return {
      generatedAt: new Date().toISOString(),
      route,
      environment: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        viewport: `${innerWidth}×${innerHeight}`,
        devicePixelRatio,
        hardwareConcurrency: navigator.hardwareConcurrency ?? null,
        deviceMemoryGB: (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? null,
        secureContext: window.isSecureContext,
        webglRenderer: gl.getParameter(gl.RENDERER) as string,
        heapUsedMB: memory ? Number((memory.usedJSHeapSize / 1048576).toFixed(2)) : null,
      },
      render: {
        sampleCount: sorted.length,
        fpsApprox: percentile(0.5) ? Number((1000 / percentile(0.5)).toFixed(1)) : 0,
        frameP50Ms: Number(percentile(0.5).toFixed(2)),
        frameP95Ms: Number(percentile(0.95).toFixed(2)),
        frameWorstMs: Number((sorted.at(-1) ?? 0).toFixed(2)),
        drawCalls: renderer.info.render.calls,
        triangles: renderer.info.render.triangles,
        geometries: renderer.info.memory.geometries,
        textures: renderer.info.memory.textures,
      },
      theme: { ...theme },
      media: { ...this.media },
      fixtures,
      notes: [...this.notes],
    };
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const anchor = document.createElement('a');
  anchor.href = URL.createObjectURL(blob);
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(anchor.href), 1000);
}

export function downloadJson(data: unknown, filename: string): void {
  downloadBlob(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }), filename);
}
